/**
 * useCommandPaletteCommands
 *
 * Builds the full commands array for the Command Palette.
 * Encapsulates all navigation + action items with permission and feature flag awareness.
 */

import {
  Activity,
  ArrowRightLeft,
  BarChart3,
  BookOpen,
  Bot,
  Building2,
  Calendar,
  FileSpreadsheet,
  FileText,
  HelpCircle,
  LayoutDashboard,
  Layers,
  Mail,
  Palette,
  PlusCircle,
  Route,
  Settings,
  Sparkles,
  Target,
  Users,
} from "lucide-react";
import React, { useMemo } from "react";

import { flags } from "@/config/featureFlags";
import { BuildConfig } from "@/context/ModeContext";
import {
  artifactBuilderDiscovery,
  buildArtifactBuilderPath,
} from "@/features/artifacts";
import { buildClientSettingsPath, buildWorkspacePath } from "@/utils/routes";

// =============================================================================
// TYPES
// =============================================================================

export type CommandCategory = "platform" | "workspace" | "admin" | "actions";

export interface CommandItem {
  id: string;
  label: string;
  description?: string;
  icon: React.ReactNode;
  shortcut?: string;
  action: () => void;
  category: CommandCategory;
  keywords?: string[];
}

// =============================================================================
// HOOK PARAMS
// =============================================================================

interface UseCommandPaletteCommandsParams {
  clientId: string | undefined;
  clientSlug: string | null;
  clientPath: string;
  canAccessAdmin: boolean;
  closeAndNavigate: (path: string) => void;
  close: () => void;
  startBuild: (config?: BuildConfig) => void;
  setShowClientSwitcher: (value: boolean) => void;
}

// =============================================================================
// HOOK
// =============================================================================

export function useCommandPaletteCommands({
  clientId,
  clientSlug,
  clientPath,
  canAccessAdmin,
  closeAndNavigate,
  close,
  startBuild,
  setShowClientSwitcher,
}: UseCommandPaletteCommandsParams): CommandItem[] {
  return useMemo<CommandItem[]>(() => {
    const items: CommandItem[] = [
      // Platform navigation
      {
        id: "dashboard",
        label: "Dashboard",
        icon: React.createElement(LayoutDashboard, { className: "h-4 w-4" }),
        action: () => closeAndNavigate("/home"),
        category: "platform",
        keywords: ["dashboard", "home", "main", "agency"],
      },
      {
        id: "clients",
        label: "Clients",
        icon: React.createElement(Building2, { className: "h-4 w-4" }),
        action: () => closeAndNavigate("/clients"),
        category: "platform",
        keywords: ["organizations", "list", "all clients"],
      },
      {
        id: "help",
        label: "Help",
        icon: React.createElement(HelpCircle, { className: "h-4 w-4" }),
        action: () => closeAndNavigate("/help"),
        category: "platform",
        keywords: ["faq", "support", "documentation", "guide"],
      },
    ];

    // Workspace navigation (only show if a client is selected)
    if (clientSlug) {
      items.push(
        {
          id: "workspace-overview",
          label: "Overview",
          icon: React.createElement(BarChart3, { className: "h-4 w-4" }),
          action: () => closeAndNavigate(clientPath),
          category: "workspace",
          keywords: ["overview", "summary", "dashboard"],
        },
        {
          id: "annual-giving",
          label: "Annual Giving",
          icon: React.createElement(Target, { className: "h-4 w-4" }),
          action: () => closeAndNavigate(`${clientPath}/annual-giving`),
          category: "workspace",
          keywords: ["annual giving", "mailer", "appeals", "campaign planning"],
        },
        {
          id: "campaigns",
          label: "Campaigns",
          icon: React.createElement(Mail, { className: "h-4 w-4" }),
          action: () => closeAndNavigate(`${clientPath}/campaigns`),
          category: "workspace",
          keywords: ["email", "direct mail", "outreach"],
        },
        {
          id: "planning-cycle",
          label: "Planning Cycle",
          icon: React.createElement(Target, { className: "h-4 w-4" }),
          action: () => closeAndNavigate(`${clientPath}/cycle`),
          category: "workspace",
          keywords: [
            "annual giving",
            "planning",
            "quarterly campaigns",
            "calendar",
          ],
        },
        {
          id: "new-proposal",
          label: artifactBuilderDiscovery.proposal.label,
          description: artifactBuilderDiscovery.proposal.description,
          icon: React.createElement(FileText, { className: "h-4 w-4" }),
          action: () =>
            closeAndNavigate(buildArtifactBuilderPath(clientSlug, "proposal")),
          category: "workspace",
          keywords: [...artifactBuilderDiscovery.proposal.keywords],
        },
        {
          id: "new-quarterly-snapshot",
          label: artifactBuilderDiscovery.quarterlySnapshot.label,
          description: artifactBuilderDiscovery.quarterlySnapshot.description,
          icon: React.createElement(FileSpreadsheet, { className: "h-4 w-4" }),
          action: () =>
            closeAndNavigate(
              buildArtifactBuilderPath(clientSlug, "quarterlySnapshot"),
            ),
          category: "workspace",
          keywords: [...artifactBuilderDiscovery.quarterlySnapshot.keywords],
        },
        {
          id: "messaging",
          label: "Messaging",
          icon: React.createElement(Layers, { className: "h-4 w-4" }),
          action: () => closeAndNavigate(`${clientPath}/strategy`),
          category: "workspace",
          keywords: ["messaging strategy", "narrative", "positioning", "copy"],
        },
        {
          id: "forecasting",
          label: "Forecasting",
          icon: React.createElement(Sparkles, { className: "h-4 w-4" }),
          action: () => closeAndNavigate(`${clientPath}/forecasting`),
          category: "workspace",
          keywords: ["forecast", "projections", "predictions", "revenue"],
        },
        ...(flags.enableCampaignWizard
          ? [
              {
                id: "blueprints",
                label: "Blueprints",
                icon: React.createElement(Sparkles, { className: "h-4 w-4" }),
                action: () => closeAndNavigate(`${clientPath}/blueprints`),
                category: "workspace" as const,
                keywords: [
                  "campaign brief",
                  "from brief",
                  "guided campaign",
                  "labs",
                ],
              },
            ]
          : []),
        {
          id: "events",
          label: "Events",
          icon: React.createElement(Calendar, { className: "h-4 w-4" }),
          action: () => closeAndNavigate(`${clientPath}/events`),
          category: "workspace",
          keywords: ["gala", "luncheon", "auction", "sponsorship"],
        },
        {
          id: "major-donors",
          label: "Major Donors",
          icon: React.createElement(Bot, { className: "h-4 w-4" }),
          action: () => closeAndNavigate(`${clientPath}/major-donors`),
          category: "workspace",
          keywords: [
            "major donors",
            "moves management",
            "corporate giving",
            "foundation relations",
          ],
        },
        {
          id: "systems",
          label: "Systems",
          icon: React.createElement(Settings, { className: "h-4 w-4" }),
          action: () => closeAndNavigate(`${clientPath}/systems`),
          category: "workspace",
          keywords: ["crm", "systems", "integrations", "data", "configuration"],
        },
        {
          id: "donor-lab",
          label: "Donor Lab",
          icon: React.createElement(Users, { className: "h-4 w-4" }),
          action: () => closeAndNavigate(`${clientPath}/donor-lab`),
          category: "workspace",
          keywords: ["donors", "analysis", "intelligence"],
        },
        {
          id: "segments",
          label: "Segments",
          icon: React.createElement(Layers, { className: "h-4 w-4" }),
          action: () => closeAndNavigate(`${clientPath}/segments`),
          category: "workspace",
          keywords: ["audience", "groups", "targeting"],
        },
        {
          id: "agent-inbox",
          label: "Agent Inbox",
          icon: React.createElement(Bot, { className: "h-4 w-4" }),
          action: () => closeAndNavigate(`${clientPath}/agent-inbox`),
          category: "workspace",
          keywords: ["ai", "recommendations", "drafts", "agents", "automation"],
        },
        // Client Settings (workspace-scoped)
        {
          id: "client-settings",
          label: "Client Settings",
          icon: React.createElement(Settings, { className: "h-4 w-4" }),
          action: () =>
            closeAndNavigate(buildClientSettingsPath(clientSlug ?? "")),
          category: "workspace",
          keywords: [
            "settings",
            "profile",
            "setup",
            "configuration",
            "upload",
            "report",
            "integrations",
          ],
        },
        {
          id: "brand-hub",
          label: "Brand Hub",
          icon: React.createElement(Palette, { className: "h-4 w-4" }),
          action: () =>
            closeAndNavigate(
              buildWorkspacePath(clientSlug ?? "", "brand-profile"),
            ),
          category: "workspace",
          keywords: [
            "branding",
            "voice",
            "style",
            "colors",
            "brand",
            "boilerplate",
            "brand profile",
          ],
        },
        {
          id: "story-bank",
          label: "Story Bank",
          description: "View and manage polished stories",
          icon: React.createElement(BookOpen, { className: "h-4 w-4" }),
          action: () => closeAndNavigate(`${clientPath}/story-bank`),
          category: "workspace" as CommandCategory,
          keywords: [
            "stories",
            "narrative",
            "story bank",
            "polished",
            "deployed",
          ],
        },
        {
          id: "worksheets",
          label: "Worksheets",
          description: "Story capture in progress",
          icon: React.createElement(BookOpen, { className: "h-4 w-4" }),
          action: () => closeAndNavigate(`${clientPath}/story-engine`),
          category: "workspace" as CommandCategory,
          keywords: [
            "story capture",
            "worksheet",
            "story development",
            "ingredients",
          ],
        },
        {
          id: "story-opportunities",
          label: "Story Opportunities",
          description: "Quarterly story planning",
          icon: React.createElement(BookOpen, { className: "h-4 w-4" }),
          action: () => closeAndNavigate(`${clientPath}/story-opportunities`),
          category: "workspace" as CommandCategory,
          keywords: ["story planning", "quarterly", "opportunity", "calendar"],
        },
        {
          id: "meeting-prep",
          label: "Meeting Prep",
          description: "Prepare for upcoming meetings",
          icon: React.createElement(Calendar, { className: "h-4 w-4" }),
          action: () => closeAndNavigate(`${clientPath}/meetings`),
          category: "workspace" as CommandCategory,
          keywords: ["meeting", "agenda", "prep", "briefing", "preparation"],
        },
        {
          id: "portfolio",
          label: "Portfolio",
          description: "Donor portfolio intelligence",
          icon: React.createElement(Users, { className: "h-4 w-4" }),
          action: () => closeAndNavigate(`${clientPath}/major-donors`),
          category: "workspace" as CommandCategory,
          keywords: [
            "donors",
            "portfolio",
            "tiers",
            "readiness",
            "major donors",
          ],
        },
        {
          id: "story-coverage",
          label: "Story Coverage",
          description: "Portfolio story gaps by donor",
          icon: React.createElement(Users, { className: "h-4 w-4" }),
          action: () =>
            closeAndNavigate(`${clientPath}/major-donors?tab=story-coverage`),
          category: "workspace" as CommandCategory,
          keywords: [
            "coverage",
            "story coverage",
            "gaps",
            "donor stories",
            "uncaptured",
          ],
        },
      );
    }

    // Admin commands (only for admin users)
    if (canAccessAdmin) {
      items.push(
        {
          id: "admin-clients",
          label: "Admin: Manage Clients",
          icon: React.createElement(Building2, { className: "h-4 w-4" }),
          action: () => closeAndNavigate("/admin/clients"),
          category: "admin",
          keywords: ["administration", "all clients", "delete"],
        },
        {
          id: "admin-monitoring",
          label: "Admin: Monitoring",
          icon: React.createElement(Activity, { className: "h-4 w-4" }),
          action: () => closeAndNavigate("/admin/monitoring"),
          category: "admin",
          keywords: ["system", "health", "logs"],
        },
        {
          id: "admin-brand-knowledge",
          label: "Admin: Brand Knowledge",
          icon: React.createElement(BookOpen, { className: "h-4 w-4" }),
          action: () => closeAndNavigate("/admin/brand-knowledge"),
          category: "admin",
          keywords: ["kb", "knowledge base"],
        },
        {
          id: "admin-journeys",
          label: "Admin: Journey Management",
          icon: React.createElement(Route, { className: "h-4 w-4" }),
          action: () => closeAndNavigate("/admin/journeys"),
          category: "admin",
          keywords: ["templates", "workflows"],
        },
      );
    }

    // Actions
    items.push(
      {
        id: "switch-workspace",
        label: "Switch Workspace",
        icon: React.createElement(ArrowRightLeft, { className: "h-4 w-4" }),
        action: () => setShowClientSwitcher(true),
        category: "actions",
        keywords: ["change", "select", "organization", "client", "workspace"],
      },
      {
        id: "account-settings",
        label: "Account Settings",
        icon: React.createElement(Settings, { className: "h-4 w-4" }),
        action: () => closeAndNavigate("/home"),
        category: "platform",
        keywords: ["preferences", "account", "profile", "user"],
      },
    );

    // Create campaign action (only when client is selected)
    if (clientId && flags.enableCampaignWizard) {
      items.push({
        id: "create-campaign",
        label: "Create Campaign",
        description: "Start a new campaign or journey",
        icon: React.createElement(PlusCircle, { className: "h-4 w-4" }),
        action: () => {
          close();
          startBuild({ source: "CommandPalette" });
        },
        category: "actions",
        keywords: [
          "new",
          "add",
          "campaign",
          "journey",
          "appeal",
          "event",
          "create",
        ],
      });
    }

    return items;
  }, [
    clientId,
    clientPath,
    clientSlug,
    canAccessAdmin,
    closeAndNavigate,
    close,
    startBuild,
    setShowClientSwitcher,
  ]);
}
